import { raidAuthoringApi } from "../../services/raidAuthoringApi";
import { messageKinds, seconds } from "../../utils/resourcePresentation";
import "./ResourceTables.css";
import { skillReferences } from "../../utils/raidAuthoring";
// 스킬 탭 - 보스 스킬 목록 관리
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Typography,
  Input,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useApp } from "../../contexts/AppContext";
import { skillApi, referenceApi } from "../../services/api";
import { supabase } from "../../lib/supabase";
import { BossSkill } from "../../types";
import SkillEffectRelationsModal from "../common/SkillEffectRelationsModal";

const { Title, Text } = Typography;

interface SkillsTabProps {
  isCommonMode?: boolean;
  bossCode?: string;
  entityCode?: string;
  onUpdate?: () => void;
}

const SkillsTab: React.FC<SkillsTabProps> = ({
  isCommonMode = false,
  bossCode,
  entityCode,
  onUpdate,
}) => {
  const { state, openSkillModal, openCopyModal } = useApp();
  const [skills, setSkills] = useState<BossSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [owner, setOwner] = useState("all");
  const [relationsModalVisible, setRelationsModalVisible] = useState(false);
  const [selectedSkillKey, setSelectedSkillKey] = useState<string>("");
  const [selectedSkillOwner, setSelectedSkillOwner] = useState("");
  const [selectedSkillName, setSelectedSkillName] = useState<string>("");
  const [triggerRelations, setTriggerRelations] = useState<any[]>([]);
  const [skillEffectRelations, setSkillEffectRelations] = useState<any[]>([]);

  // 필요한 참조 데이터
  const [targetTypes, setTargetTypes] = useState<any[]>([]);

  const loadRequest = useRef(0);
  // 현재 보스 코드
  const currentBossCode = isCommonMode
    ? "COMMON"
    : entityCode ||
      bossCode ||
      state.selectedEntity?.entity_code ||
      state.selectedBoss?.boss_code;

  const loadReferenceData = useCallback(async () => {
    try {
      const targetTypeData = await referenceApi.fetchTargetTypes();
      setTargetTypes(targetTypeData);
    } catch (error) {
      console.error("Failed to load reference data:", error);
    }
  }, []);

  const loadSkills = useCallback(async () => {
    if (!currentBossCode) return;
    const requestId = ++loadRequest.current;
    setLoading(true);
    try {
      const childResult = await supabase
        .from("raid_entity_renewed")
        .select("entity_code,entity_name")
        .eq("parent_code", currentBossCode);
      if (childResult.error) throw childResult.error;
      const children = entityCode ? [] : childResult.data || [];
      const codes = [currentBossCode, ...children.map((c) => c.entity_code)];
      const lists = await Promise.all(
        codes.map((code) => skillApi.getByBoss(code)),
      );
      const allSkills = Array.from(
        new Map(lists.flat().map((s) => [s.key, s])).values(),
      ).map((s) => ({
        ...s,
        _parent_name: children.find((c) => c.entity_code === s.boss_code)
          ?.entity_name,
      }));
      const links = await raidAuthoringApi.triggers(codes);
      const relations = allSkills.length
        ? await supabase
            .from("boss_skill_effect_relation_renewed")
            .select("*")
            .in(
              "skill",
              allSkills.map((s) => s.key),
            )
        : { data: [], error: null };
      if (relations.error) throw relations.error;
      if (requestId !== loadRequest.current) return;
      setSkills(allSkills);
      setSkillEffectRelations(relations.data || []);
      setTriggerRelations(
        links.triggers.flatMap((t) => {
          const actions = links.actions.filter((a) => a.trigger_id === t.id);
          return skillReferences(t, actions).map((skill) => ({
            skill,
            trigger: t,
            ...messageKinds(actions),
          }));
        }),
      );
    } catch (e: any) {
      if (requestId === loadRequest.current)
        message.error(e.message || "스킬 연결을 불러오지 못했습니다.");
    } finally {
      if (requestId === loadRequest.current) setLoading(false);
    }
  }, [currentBossCode, entityCode]);
  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);
  useEffect(() => {
    setOwner("all");
    loadSkills();
    const refresh = () => {
      loadSkills();
    };
    const events = ["skillsUpdated", "effectsUpdated", "triggersUpdated"];
    events.forEach((name) => window.addEventListener(name, refresh));
    return () => {
      loadRequest.current++;
      events.forEach((name) => window.removeEventListener(name, refresh));
    };
  }, [loadSkills]);

  // 스킬 삭제
  const handleDelete = async (skillKey: string) => {
    try {
      await skillApi.delete(skillKey);
      message.success("스킬이 삭제되었습니다.");
      loadSkills();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("스킬 삭제 실패:", error);
      message.error("스킬 삭제에 실패했습니다.");
    }
  };

  const getTargetType = (code: string) =>
    targetTypes.find((t) => t.target_code === code)?.target_name ||
    code ||
    "미설정";
  const linksFor = (key: string) =>
    triggerRelations.filter((r) => r.skill === key);
  const effectsFor = (key: string) =>
    skillEffectRelations.filter((r) => r.skill === key);
  const filtered = skills.filter(
    (s) =>
      (owner === "all" || s.boss_code === owner) &&
      [s.name, s.key, (s as any)._parent_name].some((v) =>
        v?.toLowerCase().includes(searchText.toLowerCase()),
      ),
  );
  const columns: ColumnsType<BossSkill> = [
    {
      title: "스킬",
      key: "name",
      width: 240,
      render: (_, s) => (
        <div className="resource-name">
          <Text strong>{s.name}</Text>
          <Text type="secondary">
            {(s as any)._parent_name || (isCommonMode ? "공통" : "본체")}
          </Text>
        </div>
      ),
    },
    {
      title: "시전 / 재사용",
      key: "timing",
      width: 150,
      render: (_, s) => (
        <div>
          시전 {seconds(s.casting_time_s)}
          <br />
          <Text type="secondary">
            재사용 {seconds(s.self_cooldown_s, "없음")}
          </Text>
        </div>
      ),
    },
    { title: "대상", dataIndex: "target", width: 120, render: getTargetType },
    {
      title: "알림",
      key: "warnings",
      width: 150,
      render: (_, s) => {
        const links = linksFor(s.key);
        const flags = [
          s.warning_ground && "바닥",
          s.warning_cast_bar && "시전 바",
          (s.warning_text || links.some((r) => r.warning)) && "경고",
          links.some((r) => r.dialogue) && "대사",
          s.warning_sound && "소리",
          s.warning_screen && "화면",
        ];
        return (
          <Space wrap size={4}>
            {flags.filter(Boolean).map((f) => (
              <Tag key={String(f)}>{f}</Tag>
            ))}
            {!flags.some(Boolean) && <Text type="secondary">없음</Text>}
          </Space>
        );
      },
    },
    {
      title: "연결",
      key: "links",
      width: 140,
      render: (_, s) => (
        <Space direction="vertical" size={0}>
          <Text>트리거 {linksFor(s.key).length}개</Text>
          <Text type="secondary">효과 {effectsFor(s.key).length}개</Text>
        </Space>
      ),
    },
    {
      title: "작업",
      key: "actions",
      fixed: "right",
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="편집">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openSkillModal(record);
                if (onUpdate) onUpdate();
              }}
            />
          </Tooltip>
          <Tooltip title="효과 연결">
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => {
                setSelectedSkillKey(record.key);
                setSelectedSkillOwner(record.boss_code);
                setSelectedSkillName(record.name);
                setRelationsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="복사">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => openCopyModal("skill", record, record.name)}
            />
          </Tooltip>
          <Popconfirm
            title="삭제 확인"
            description="이 스킬을 삭제하시겠습니까?"
            onConfirm={() => handleDelete(record.key)}
            okText="삭제"
            cancelText="취소"
          >
            <Tooltip title="삭제">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <div className="resource-page">
      <div className="resource-toolbar">
        <div>
          <Title level={4}>{isCommonMode ? "공통 스킬" : "스킬"}</Title>
          <Text type="secondary">
            {skills.length}개 · 행을 펼치면 코드와 연결 상세를 확인할 수
            있습니다.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openSkillModal()}
        >
          스킬 추가
        </Button>
      </div>
      <Space wrap className="resource-filters">
        <Input.Search
          aria-label="스킬 검색"
          placeholder="이름 또는 코드 검색"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 260 }}
        />
        <Select
          aria-label="스킬 소유자"
          value={owner}
          onChange={setOwner}
          style={{ minWidth: 170 }}
          options={[
            { value: "all", label: "전체 몬스터" },
            ...Array.from(
              new Map(
                skills.map((s) => [
                  s.boss_code,
                  {
                    value: s.boss_code,
                    label:
                      (s as any)._parent_name ||
                      (isCommonMode ? "공통" : "본체"),
                  },
                ]),
              ).values(),
            ),
          ]}
        />
      </Space>
      <Table
        className="resource-table"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        rowKey="key"
        scroll={{ x: 950 }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showTotal: (n) => n + "개",
        }}
        expandable={{
          expandedRowRender: (s) => (
            <div className="resource-details">
              <Text code copyable>
                {s.key}
              </Text>
              <p>{s.description || "설명 없음"}</p>
              <Space wrap>
                <Tag>첫 사용 {seconds(s.initial_cooldown_s)}</Tag>
                <Tag>채널링 {seconds(s.channeling_time_s, "없음")}</Tag>
                <Tag>
                  시전 중단{" "}
                  {s.is_interruptable == null
                    ? "미설정"
                    : s.is_interruptable
                      ? "가능"
                      : "불가"}
                </Tag>
                <Tag>사거리 {s.range ?? "미설정"}</Tag>
                <Tag>
                  범위 {s.area || "미설정"} / {s.area_radius ?? "미설정"}
                </Tag>
              </Space>
              <p>
                <Text strong>연결된 트리거</Text>
              </p>
              <Space wrap>
                {linksFor(s.key).map((r) => (
                  <Tag key={r.trigger.id}>{r.trigger.trigger_name}</Tag>
                ))}
                {!linksFor(s.key).length && (
                  <Text type="secondary">
                    현재 범위에서 연결된 트리거가 없습니다.
                  </Text>
                )}
              </Space>
              <p>
                <Text strong>연결된 효과</Text>
              </p>
              <Space wrap>
                {effectsFor(s.key).map((r, i) => (
                  <Tag key={i}>
                    {r.effect} · {seconds(r.duration_s, "즉시 적용")}
                  </Tag>
                ))}
                {!effectsFor(s.key).length && (
                  <Text type="secondary">연결된 효과가 없습니다.</Text>
                )}
              </Space>
            </div>
          ),
        }}
      />
      <SkillEffectRelationsModal
        visible={relationsModalVisible}
        onCancel={() => {
          setRelationsModalVisible(false);
          loadSkills();
        }}
        skillKey={selectedSkillKey}
        skillName={selectedSkillName}
        bossCode={selectedSkillOwner || currentBossCode || ""}
      />
    </div>
  );
};
export default SkillsTab;
